import { injectSummaryStepCompleteness, SUMMARY_STEPS } from './summary.util';
import { computed, inject, Injectable, signal } from '@angular/core';
import { UmmeldungStore } from '../ummeldung.store';
import { ConditionOutcome } from '@mwe/features/wizard';
import { map, Observable, of, switchMap } from 'rxjs';
import { Ummeldung } from '../../../../generated/model/ummeldung.model';
import { catchError, tap } from 'rxjs/operators';

@Injectable()
export class UmmeldungSubmitService {
  private stepCompleteness = injectSummaryStepCompleteness();
  private ummeldungStore = inject(UmmeldungStore);

  private _isSuccessfullySubmitted = signal(false);
  private _isSubmitTriggered = signal(false);
  private _isSubmitting = signal(false);
  private _isSubmitError = signal(false);

  public canSubmit = computed(() => {
    const steps = SUMMARY_STEPS.map(step => ({ ...step, isComplete: this.stepCompleteness()[step.id] }));
    return steps.every(step => step.isComplete);
  });

  public isSuccessfullySubmitted = this._isSuccessfullySubmitted.asReadonly();
  public isSubmitTriggered = this._isSubmitTriggered.asReadonly();
  public isSubmitting = this._isSubmitting.asReadonly();
  public isSubmitError = this._isSubmitError.asReadonly();

  submit(): Observable<ConditionOutcome> {
    this._isSubmitTriggered.set(true);
    this._isSubmitError.set(false);
    if (this.isSubmitting() || !this.canSubmit()) {
      return of(ConditionOutcome.INVALID);
    }
    this._isSubmitting.set(true);
    return this.ummeldungStore.saveActiveUmmeldung(Ummeldung.AktuellerProzessSchrittEnum.ZUSAMMENFASSUNG).pipe(
      switchMap(() => this.ummeldungStore.submitUmmeldung(this.ummeldungStore.activeUmmeldung.id())),
      map(() => ConditionOutcome.VALID),
      tap(() => {
        this._isSubmitting.set(false);
        this._isSuccessfullySubmitted.set(true);
      }),
      catchError(() => {
        this.ummeldungStore.clearError();
        this._isSubmitError.set(true);
        this._isSubmitting.set(false);
        return of(ConditionOutcome.INVALID);
      }),
    );
  }

  public dismissSubmitError() {
    this._isSubmitError.set(false);
  }

  public dismissSubmitTriggered() {
    this._isSubmitTriggered.set(false);
  }

  reset() {
    this._isSubmitTriggered.set(false);
    this._isSubmitting.set(false);
    this._isSubmitError.set(false);
    this._isSuccessfullySubmitted.set(false);
  }
}
